import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, RefreshCw, Filter, Info } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/saarv-admin`;

const RESULT_BADGE = {
  success: 'bg-green-100 text-green-800',
  failure: 'bg-red-100 text-red-800',
  partial: 'bg-yellow-100 text-yellow-800',
  denied: 'bg-orange-100 text-orange-800',
};

function prettyEvent(eventType) {
  if (!eventType) return '—';
  return eventType
    .split('.')
    .map(part => part.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    .join(' → ');
}

function ExpandedDetails({ entry }) {
  const fields = [
    ['Description', entry.description],
    ['Target', entry.target_name || entry.target_id],
    ['IP', entry.ip_address],
    ['User-Agent', entry.user_agent],
    ['Request ID', entry.request_id],
    ['Error', entry.error_message],
  ].filter(([, v]) => v != null && v !== '');

  const details = entry.details && typeof entry.details === 'object' ? entry.details : null;

  return (
    <div className="bg-gray-50 px-4 py-3 text-xs space-y-2">
      {fields.length > 0 && (
        <table className="w-full">
          <tbody>
            {fields.map(([label, value]) => (
              <tr key={label}>
                <td className="font-semibold text-gray-600 pr-3 py-0.5 align-top w-24">{label}</td>
                <td className="text-gray-800 break-all">{String(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {details && (
        <div>
          <div className="font-semibold text-gray-600 mb-1">Details</div>
          <pre className="bg-white border border-gray-200 rounded p-2 text-[11px] text-gray-700 overflow-x-auto">
            {JSON.stringify(details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function PAAuditLogs() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pageInput, setPageInput] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const currentPage = Math.floor(skip / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : skip + 1;
  const showingTo = Math.min(skip + entries.length, total);
  const canPrev = skip > 0 && !loading;
  const canNext = skip + pageSize < total && !loading;

  useEffect(() => {
    const adminData = localStorage.getItem('platform_admin');
    const token = localStorage.getItem('platform_admin_token');
    if (!adminData || !token) {
      navigate('/saarv-admin/login');
      return;
    }
    try {
      setAdmin(JSON.parse(adminData));
    } catch {
      navigate('/saarv-admin/login');
    }
  }, [navigate]);

  const fetchTenants = useCallback(async () => {
    try {
      const token = localStorage.getItem('platform_admin_token');
      const res = await axios.get(`${API_BASE}/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 },
      });
      setTenants(res.data?.tenants || []);
    } catch (e) {
      // non-fatal — the filter dropdown will be empty
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('platform_admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const url = tenantFilter
        ? `${API_BASE}/tenants/${tenantFilter}/audit-logs`
        : `${API_BASE}/audit-logs`;
      const params = { skip, limit: pageSize };
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      const res = await axios.get(url, { headers, params });
      setEntries(res.data?.logs || []);
      setTotal(typeof res.data?.total === 'number' ? res.data.total : 0);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem('platform_admin');
        localStorage.removeItem('platform_admin_token');
        navigate('/saarv-admin/login');
        return;
      }
      setError(e.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [skip, pageSize, eventTypeFilter, tenantFilter, navigate]);

  const goToPage = (page1Based) => {
    if (loading) return;
    const clamped = Math.max(1, Math.min(totalPages, page1Based));
    setSkip((clamped - 1) * pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    // Keep first visible row of the current page roughly in view by
    // recomputing skip against the new page size.
    const firstVisible = skip;
    setPageSize(newSize);
    setSkip(Math.floor(firstVisible / newSize) * newSize);
    setPageInput('');
  };

  const handlePageJump = (e) => {
    e.preventDefault();
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n)) {
      goToPage(n);
      setPageInput('');
    }
  };

  useEffect(() => { fetchTenants(); }, [fetchTenants]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/saarv-admin/dashboard')}
            className="text-gray-500 hover:text-gray-800"
            title="Back to dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Platform Audit Logs</h1>
        </div>
        <div className="text-sm text-gray-500">
          {admin.email}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3 mb-4 text-sm flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            These logs cover all platform-admin actions (tenant create/suspend/delete,
            module enablement, admin logins, password changes). Tenant-internal user
            actions are stored in each tenant&rsquo;s own audit log and are visible to
            that tenant&rsquo;s admin.
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={tenantFilter}
                onChange={(e) => { setTenantFilter(e.target.value); setSkip(0); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All tenants</option>
                {tenants.map(t => (
                  <option key={t.id || t.company_id} value={t.id || t.company_id}>
                    {t.company_name || t.name || t.id}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="event_type filter (e.g. tenant_created)"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); fetchLogs(); } }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
              />
            </div>
            <button
              onClick={() => { setSkip(0); fetchLogs(); }}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Actor</th>
                  <th className="px-3 py-2 text-left">Event</th>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-left">Result</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td className="px-3 py-4 text-gray-400" colSpan={7}>Loading&hellip;</td></tr>
                ) : entries.length === 0 ? (
                  <tr><td className="px-3 py-4 text-gray-400" colSpan={7}>No entries.</td></tr>
                ) : entries.map((e) => {
                  const isOpen = expandedId === e.id;
                  const tenantName = tenants.find(t => (t.id || t.company_id) === e.tenant_id)?.company_name;
                  return (
                    <React.Fragment key={e.id}>
                      <tr className="hover:bg-gray-50 align-top">
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">{formatDateTime(e.event_time || e.timestamp)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-800">{e.actor_email || e.actor_id || '—'}</div>
                          <div className="text-xs text-gray-500">{e.actor_type || 'platform_admin'}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{prettyEvent(e.event_type)}</td>
                        <td className="px-3 py-2">
                          <div className="text-gray-700 truncate max-w-[160px]" title={tenantName || ''}>
                            {tenantName || '—'}
                          </div>
                          <div className="text-xs text-gray-400 truncate max-w-[160px]" title={e.tenant_id || ''}>
                            {e.tenant_id || ''}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-700">{e.target_type || '—'}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[180px]" title={e.target_id || ''}>
                            {e.target_name || e.target_id || ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${RESULT_BADGE[e.result] || 'bg-gray-100 text-gray-700'}`}>
                            {e.result || 'success'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setExpandedId(isOpen ? null : e.id)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            {isOpen ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={7} className="p-0"><ExpandedDetails entry={e} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
            <div className="flex items-center gap-3">
              <span>
                {total > 0
                  ? <>Showing <span className="font-semibold">{showingFrom}</span>&ndash;<span className="font-semibold">{showingTo}</span> of <span className="font-semibold">{total.toLocaleString()}</span></>
                  : 'No entries'}
              </span>
              <span className="flex items-center gap-1.5 text-gray-500">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
                  disabled={loading}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(1)}
                disabled={!canPrev}
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                title="First page"
              >
                «
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={!canPrev}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-gray-700">
                Page{' '}
                <span className="font-semibold">{currentPage}</span>{' '}
                of <span className="font-semibold">{totalPages.toLocaleString()}</span>
              </span>
              <form onSubmit={handlePageJump} className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  placeholder="Go to"
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                  disabled={loading || totalPages <= 1}
                />
              </form>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!canNext}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Next
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={!canNext}
                className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                title="Last page"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
